import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { map, compact, merge, get } from 'lodash';
import SpinIcon from 'common/spinners/SpinIcon';
import moment from 'moment';
import {
  FilterDuration,
  Pager,
  Icon,
} from '@unite-us/app-components';
import { shapes } from '@unite-us/client-utils';
import FilterList from 'common/display/FilterList';
import SimpleCount from '../SimpleCount';
import CappedCount from '../CappedCount';
import useDateRangeFilter from './hooks/useDateRangeFilter';
import FilterBarItem from './components/FilterBarItem';

const SecondaryFilters = ({ onMoreFiltersClick }) => (
  <div className="filter-bar__more-options">
    <div
      className="filter-bar__more-filters"
      onClick={onMoreFiltersClick}
      onKeyDown={() => { }}
      role="link"
      tabIndex={0}
    >
      <Icon
        className="filter-toggle__icon"
        icon="Filter"
        size={15}
      />
      <a
        tabIndex={0}
        className="ml-half filter-bar__more-options"
        id="filter-bar-more-options"
        role="button"
      >
        More Filters
      </a>
    </div>
  </div>
);
SecondaryFilters.propTypes = {
  onMoreFiltersClick: PropTypes.func,
};
SecondaryFilters.defaultProps = {
  onMoreFiltersClick: () => { },
};

const FilterBar = ({
  asyncSearch,
  filters,
  isFetching,
  itemLabel,
  items,
  topMargin,
  onFiltersChange,
  onItemClick,
  onMoreFiltersClick,
  onNextClick,
  onPrevClick,
  paging,
  showItemFilter,
  showSecondaryFilters,
  styles,
  dataTestId,
  showFilterDuration,
  addDateRangeFilter,
  simplePaging,
}) => {
  const filterDuration = useRef();
  const dateRangeFilterPresent = showFilterDuration && addDateRangeFilter;
  const totalCountCapped = get(paging, 'total_count_capped', false);

  const {
    startDateField,
    endDateField,
    startCallbacks,
    endCallbacks,
  } = useDateRangeFilter(dateRangeFilterPresent, filterDuration, onFiltersChange);

  return (
    <section
      className="filter-bar" data-testid={dataTestId}
      aria-labelledby="filters-heading"
    >
      <h2 className="sr-only" id="filters-heading">Filters</h2>
      <div className="filter-bar__wrapper">
        <div className="filters mr-5">
          {
            map(compact(filters), (f) => (
              <FilterBarItem
                key={f.key}
                filter={f}
                asyncSearch={asyncSearch}
                styles={styles}
                onFiltersChange={onFiltersChange}
              />
            ))
          }
          {
            showSecondaryFilters && <SecondaryFilters onMoreFiltersClick={onMoreFiltersClick} />
          }
          {
          dateRangeFilterPresent && (
            <FilterDuration
              ref={filterDuration}
              minDate={'01/01/2012'}
              maxDate={moment().format('MM/DD/YYYY')}
              startField={merge(startDateField, startCallbacks)}
              endField={merge(endDateField, endCallbacks)}
            />
          )
        }
        </div>

        <div className={topMargin ? 'mt-5 pager-container' : 'pager-container'}>
          {
            isFetching && (
              <SpinIcon
                iconStyle={styles.spinIconIconStyles}
                style={styles.spinIconStyles}
              />
            )
          }
          <div className="flex items-center">
            {simplePaging && (
              <SimpleCount
                currentPage={paging.current_page}
                nextPage={paging.next_page}
                per={paging.per}
                pageLength={paging.page_length}
              />
            )}
            {totalCountCapped && (
              <CappedCount
                currentPage={paging.current_page}
                per={paging.per}
                totalCount={paging.total_count}
              />
            )}
            <Pager
              paging={paging}
              disabled={isFetching}
              hideCount={simplePaging || totalCountCapped}
              onNextClick={onNextClick}
              onPrevClick={onPrevClick}
            />
          </div>
        </div>
      </div>

      {
        showItemFilter && (
          <FilterList
            className="filter-bar__alpha-filter-list mb-2 ml-2"
            items={items}
            itemLabel={itemLabel}
            onItemClick={onItemClick}
          />
        )
      }
    </section>
  );
};

FilterBar.propTypes = {
  filters: PropTypes.array.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  // shapes.paging already bakes in isRequired upstream, so this is genuinely required
  // eslint-disable-next-line react/require-default-props
  paging: shapes.paging,
  onMoreFiltersClick: PropTypes.func,
  topMargin: PropTypes.bool,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  onItemClick: PropTypes.func,
  showSecondaryFilters: PropTypes.bool,
  showItemFilter: PropTypes.bool,
  items: PropTypes.array,
  itemLabel: PropTypes.string,
  styles: PropTypes.object,
  asyncSearch: PropTypes.func,
  dataTestId: PropTypes.string,
  showFilterDuration: PropTypes.bool,
  addDateRangeFilter: PropTypes.bool,
  simplePaging: PropTypes.bool,
};

FilterBar.defaultProps = {
  topMargin: true,
  showItemFilter: false,
  showSecondaryFilters: false,
  styles: {
    spinIconStyles: {
      marginTop: '7px',
      marginRight: '10px',
    },
    spinIconIconStyles: {
      height: '20px',
      width: '20px',
    },
  },
  onMoreFiltersClick: () => { },
  onItemClick: undefined,
  items: [],
  itemLabel: '',
  simplePaging: false,
  asyncSearch: undefined,
  dataTestId: '',
  showFilterDuration: false,
  addDateRangeFilter: false,
};

export default FilterBar;
